import { refreshAuthToken } from 'app/providers/with-wagmi-auth/utils';
import { dateLogger, retrieveStoredAuthTokens } from 'shared/lib';

type RefreshResolver = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

export class RefreshTokenManager {
  private static _instance: RefreshTokenManager;

  private isRefreshing = false;

  private refreshQueue: RefreshResolver[] = [];

  // eslint-disable-next-line no-useless-constructor
  private constructor() {}

  public static get instance(): RefreshTokenManager {
    if (!RefreshTokenManager._instance) {
      RefreshTokenManager._instance = new RefreshTokenManager();
    }
    return RefreshTokenManager._instance;
  }

  public async refreshToken(scope = 'unknown'): Promise<string> {
    dateLogger(`Refresh requested: ${scope}`);

    const promise = new Promise<string>((resolve, reject) => {
      this.refreshQueue.push({ resolve, reject });
      dateLogger(`Queued refresh request: ${scope}, queue length: ${this.refreshQueue.length}`);
    });

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      dateLogger(`No refresh in progress, starting new: ${scope}`);
      this.startRefresh(scope).catch((error) => dateLogger(`Unexpected error in startRefresh:`, error));
    } else {
      dateLogger(`Refresh already in progress, queuing: ${scope}`);
    }

    return promise;
  }

  private async startRefresh(scope: string): Promise<void> {
    try {
      const { access } = await refreshAuthToken();
      dateLogger(`Refresh succeeded: ${scope}, new token: ${access}`);

      this.refreshQueue.forEach(({ resolve }) => resolve(access));
      this.refreshQueue = [];
    } catch (error) {
      dateLogger(`Refresh failed: ${scope}`, error);
      this.refreshQueue.forEach(({ reject }) => reject(error));
      this.refreshQueue = [];
      throw error;
    } finally {
      this.isRefreshing = false;
      dateLogger(`Refresh lock released: ${scope}`);
    }
  }
}

export const refreshTokenWithLock = async (scope?: string) => RefreshTokenManager.instance.refreshToken(scope);
